import { useContext } from 'react'
import { ProductContext } from 'src/components/restructure/product/contexts'
import {
  getProgressiveDiscountPricing,
  hasAppliedProgressiveDiscount,
  isListPriceHigherThan,
} from 'src/components/restructure/product/utils/progressiveDiscountPricing'
import { formatterPrice } from 'src/utils/formatterPrice'
import { MoneyDollarBoxIcon } from 'src/components/Icons/MoneyDollarBoxIcon'
import { Statsig } from 'src/sdk/statsig'
import { PROGRESSIVE_DISCOUNT_EXPERIMENT_ID } from 'src/components/common/StatsigExposure/StatsigExposure'

import { InstallmentV2 } from './Installments/InstallmentsV2'

export function ProductPricingV2() {
  const isProgressiveDiscountEnabled = Statsig.useStatsigFeatureGateValue(
    PROGRESSIVE_DISCOUNT_EXPERIMENT_ID
  )

  const { currentSku, progressiveDiscount, quantity } =
    useContext(ProductContext)

  const {
    sellerDefault: { sellerOffer, sellerId },
  } = currentSku

  const cashPaymentLabel = sellerId === '1' ? 'No Pix ou à vista' : 'À vista'

  const progressivePricing = getProgressiveDiscountPricing({
    progressiveDiscount,
    itemId: currentSku.itemId,
    quantity,
  })

  const isProgressiveApplied =
    !!isProgressiveDiscountEnabled &&
    hasAppliedProgressiveDiscount(progressivePricing)

  const hasPixDiscount =
    !isProgressiveApplied && Boolean(sellerOffer.cashDiscountPercentage)

  const hasPriceCutOnly =
    !isProgressiveApplied && !hasPixDiscount && sellerOffer.hasDiscount

  const hasNoDiscount =
    !isProgressiveApplied && !hasPixDiscount && !sellerOffer.hasDiscount

  const progressiveUnitPrice =
    progressivePricing.discountedUnitPrice === null
      ? null
      : progressivePricing.discountedUnitPrice / 100

  const priceFormatted = formatterPrice(
    isProgressiveApplied && progressiveUnitPrice !== null
      ? progressiveUnitPrice
      : sellerOffer.price
  )

  const cashPriceFormatted = formatterPrice(
    isProgressiveApplied && progressiveUnitPrice !== null
      ? progressiveUnitPrice
      : sellerOffer.cashPrice
  )

  const cashbackValue = formatterPrice(sellerOffer.cashbackValue ?? 0)
  const discountAmount = hasPixDiscount
    ? formatterPrice(sellerOffer.price - sellerOffer.cashPrice).replace(
        /\s/g,
        ''
      )
    : sellerOffer.hasDiscount
    ? formatterPrice(sellerOffer.discount).replace(/\s/g, '')
    : ''

  const progressiveDiscountBadge =
    progressivePricing.discountPercentage > 0
      ? `${progressivePricing.discountPercentage}% OFF`
      : null

  const showProgressivePreviousPrice =
    progressiveUnitPrice !== null &&
    isListPriceHigherThan(sellerOffer.listPrice, progressiveUnitPrice)

  const renderProgressivePricing = () => (
    <div className="flex items-center mt-lg">
      <div className="flex flex-col">
        {showProgressivePreviousPrice && (
          <p className="text-sm leading-[28px] font-medium text-tertiary line-through restructure-small-desktop:leading-[21px] restructure-small-desktop:font-normal">
            {formatterPrice(sellerOffer.listPrice)}
          </p>
        )}
        <span className="desktop-heading-title5 mb-3 text-restructure-primary flex items-center gap-2 font-['Inter', sans-serif] text-[20px] font-semibold leading-[100%]">
          {priceFormatted}
          {progressiveDiscountBadge && (
            <span className="w-fit bg-redDiscount text-xs leading-[18px] px-1 py-[2px] font-medium text-white">
              {progressiveDiscountBadge}
            </span>
          )}
        </span>
      </div>
    </div>
  )

  const renderPixDiscountPricing = () => (
    <div className="flex flex-col items-start mt-lg">
      <div className="min-w-[255px] flex flex-col bg-pdpPricingPixBackground mb-4 border-l-4 border-pdpPricingPixAccent px-[18px] py-[13px]">
        <p className="uppercase text-xs font-semibold text-pdpPricingPixAccent pb-1">
          {cashPaymentLabel}
        </p>
        <span className="desktop-heading-title5 mb-[5px] text-restructure-primary flex items-center gap-2 font-['Inter', sans-serif] text-[20px] font-semibold leading-[100%]">
          {cashPriceFormatted}

          {(sellerOffer.hasDiscount || isProgressiveApplied) && (
            <p className="w-fit bg-redDiscount text-xs leading-[18px] px-1 py-[2px] font-medium text-white">
              {isProgressiveApplied
                ? progressiveDiscountBadge
                : `${sellerOffer.cashDiscountPercentage}% OFF`}
            </p>
          )}
        </span>
        {discountAmount && (
          <p className="text-pdpPricingPixAccent text-xs !font-medium">
            Você economiza {discountAmount}
          </p>
        )}
      </div>
      <InstallmentV2 />
    </div>
  )

  const renderPriceCutPricing = () => (
    <div className="flex items-center mt-lg">
      <div className="flex flex-col">
        <p className="text-sm leading-[28px] font-medium text-tertiary line-through restructure-small-desktop:leading-[21px] restructure-small-desktop:font-normal">
          {formatterPrice(sellerOffer.listPrice)}
        </p>
        <span className="desktop-heading-title5 mb-3 text-restructure-primary flex items-center gap-2 font-['Inter', sans-serif] text-[20px] font-semibold leading-[100%]">
          {priceFormatted}
          <span className="w-fit bg-redDiscount text-xs leading-[18px] px-1 py-[2px] font-medium text-white">
            {sellerOffer.discountPercentage}% OFF
          </span>
        </span>
        <InstallmentV2 />
      </div>
    </div>
  )

  const renderRegularPricing = () => (
    <div className="flex items-center mt-lg">
      <div className="flex flex-col">
        <span className="desktop-heading-title5 mb-3 text-restructure-primary flex items-center gap-2 font-['Inter', sans-serif] text-[20px] font-semibold leading-[100%]">
          {priceFormatted}
        </span>
        <InstallmentV2 />
      </div>
    </div>
  )

  return (
    <div className={`flex flex-col  ${hasPixDiscount ? 'gap-4' : 'gap-2'}`}>
      {currentSku.isAvailable ? (
        <>
          {isProgressiveApplied && renderProgressivePricing()}
          {hasPixDiscount && renderPixDiscountPricing()}
          {hasPriceCutOnly && renderPriceCutPricing()}
          {hasNoDiscount && renderRegularPricing()}
          {(sellerOffer.cashbackValue ?? 0) > 0 && (
            <div className="flex items-center justify-center gap-[7px] bg-restructure-background-cashback text-xs text-textSuccess font-medium py-1 px-4">
              <MoneyDollarBoxIcon />
              <span>
                <strong>+{cashbackValue} </strong> de volta de cashback
              </span>
            </div>
          )}
        </>
      ) : (
        <h2 className="desktop-heading-title5 text-restructure-primary flex items-center gap-[24px] font-['Inter', sans-serif] text-[20px] py-[24px] font-semibold leading-[100%]">
          Produto indisponível
        </h2>
      )}
    </div>
  )
}
